import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  IndianRupee, Layers, MapPin, Users,
  Activity, ChevronLeft, ChevronRight, Home, FileText, Database, ChevronDown, EyeOff, BookOpen, Eye, LogOut, Award
} from "lucide-react";
import { useFilters } from "../context/FilterContext";
import { useAuth } from "../context/AuthContext";
import GlobalFilters from "./GlobalFilters";
import LastSyncBadge from "./LastSyncBadge";
import { toast } from "sonner";
import axios from "axios";

const MAIN_NAV = [
  { path: "/trustee", label: "Trustee Overview", icon: Award },
  { path: "/executive", label: "Executive Overview", icon: Home },
  { path: "/financial", label: "Financial", icon: IndianRupee },
  { path: "/geographic", label: "Geographic Footprint", icon: MapPin },
  { path: "/thematic", label: "Thematic Overview", icon: Layers },
  { path: "/grantee", label: "Grantee Overview", icon: Users },
  { path: "/grant-overview", label: "Grants", icon: Eye },
];

const HIDDEN_NAV = [
  { path: "/real-trustee", label: "Real-Trustee", icon: Award },
  { path: "/real-executive", label: "Real-Executive", icon: Home },
  { path: "/executive-old", label: "Executive Overview (Old)", icon: Home },
  { path: "/logics", label: "Logics", icon: BookOpen },
  { path: "/data-health", label: "Data Health", icon: Activity },
  { path: "/raw-data", label: "Raw Data", icon: Database },
];

const ALL_NAV = [...MAIN_NAV, ...HIDDEN_NAV];

const getPageTitle = (pathname) => {
  const item = ALL_NAV.find(n => n.path === pathname);
  return item?.label || "Dashboard";
};

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const { filterOptions, globalLoading, API, filters, setFilters, viewMode, setViewMode } = useFilters();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  const isOnHiddenPage = HIDDEN_NAV.some(n => n.path === location.pathname);

  // Trustee route uses a simplified filter bar that hides viewMode + status +
  // trust + grantee controls. Those state fields still get sent to every API
  // call from useCommandCentreData, so if a user set them elsewhere (e.g.
  // "New" view or Status=Closed on Executive/Financial, or a specific trust /
  // grantee drilldown) the Trustee page would silently apply them with no way
  // for the trustee to see or clear them. Reset all four hidden filters to
  // their canonical defaults whenever /trustee mounts, so trustees always see
  // the same portfolio-wide view. Visible filters (fy, theme) are preserved.
  //
  // When a reset actually happens, surface a brief toast so the user
  // understands why their previous selections didn't follow them. The toast
  // exposes an "Undo" action which restores the pre-reset state and navigates
  // away from /trustee (otherwise this same effect would re-reset the
  // filters on the next render).
  useEffect(() => {
    if (location.pathname !== "/trustee" && location.pathname !== "/real-trustee") return;

    // Snapshot the exact state we're about to overwrite so Undo can restore it.
    const prev = {
      viewMode,
      status: filters.status,
      trust: filters.trust,
      grantee: filters.grantee,
    };

    const resets = [];
    if (prev.viewMode !== "active") resets.push(prev.viewMode === "snapshot" ? "View: New" : "View: Ongoing");
    if (prev.status) resets.push(`Status: ${prev.status}`);
    if (prev.trust) resets.push(`Fund: ${prev.trust}`);
    if (prev.grantee) resets.push(`Grantee: ${prev.grantee}`);
    if (!resets.length) return;

    if (prev.viewMode !== "active") setViewMode("active");
    if (prev.status || prev.trust || prev.grantee) {
      setFilters((f) => ({ ...f, status: "", trust: "", grantee: "" }));
    }

    toast("Filters reset for Trustee view", {
      description: `Trustee Overview always shows the full portfolio, so we cleared: ${resets.join(" · ")}`,
      duration: 8000,
      action: {
        label: "Undo",
        onClick: () => {
          // Restore the pre-reset state.
          setViewMode(prev.viewMode);
          setFilters((f) => ({
            ...f,
            status: prev.status,
            trust: prev.trust,
            grantee: prev.grantee,
          }));
          // Navigate away from /trustee so the reset effect doesn't fire
          // again on the very next render. Prefer history-back; fall back
          // to /executive which exposes all filter controls.
          const wentBack = window.history.length > 1;
          if (wentBack) navigate(-1);
          else navigate("/executive");
        },
      },
    });
  }, [location.pathname, viewMode, setViewMode, filters.status, filters.trust, filters.grantee, setFilters, navigate]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const base = API.replace(/\/api\/?$/, '');
        const [ver] = await Promise.all([
          axios.get(`${base}/api/version`, { withCredentials: true }),
        ]);
        setAppVersion(ver.data);
      } catch { /* silent */ }
    };
    fetchMeta();
  }, [API]);

  const renderNavItem = (item) => (
    <NavLink
      key={item.path}
      to={item.path}
      data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2 py-1.5 rounded-md mb-0.5 transition-all duration-150 group ${
          isActive
            ? "bg-red-50 text-[#B71C1C] font-medium border-l-3 border-[#B71C1C]"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        } ${collapsed ? "justify-center" : ""}`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon size={16} strokeWidth={isActive ? 2 : 1.5} className="flex-shrink-0" />
          {!collapsed && (
            <span className="text-xs truncate">{item.label}</span>
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 shadow-sm ${collapsed ? "w-14" : "w-44"}`}
        style={{ flexShrink: 0 }}
      >
        {/* Nav */}
        <nav className="flex-1 py-3 px-1.5 overflow-y-auto">
          {MAIN_NAV.map(renderNavItem)}

          {/* Hidden Pages Group */}
          {!collapsed ? (
            <div className="mt-2">
              <button
                onClick={() => setHiddenOpen(o => !o)}
                data-testid="hidden-pages-toggle"
                className="flex items-center gap-2 px-2 py-1.5 w-full rounded-md text-slate-600 hover:text-slate-600 hover:bg-slate-50 transition-all duration-150"
              >
                <EyeOff size={14} className="flex-shrink-0" />
                <span className="text-[11px] font-medium truncate">Hidden Pages</span>
                <ChevronDown size={12} className={`ml-auto transition-transform duration-200 ${hiddenOpen || isOnHiddenPage ? "rotate-180" : ""}`} />
              </button>
              {(hiddenOpen || isOnHiddenPage) && (
                <div className="ml-1 border-l border-slate-100 pl-1 mt-0.5">
                  {HIDDEN_NAV.map(renderNavItem)}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="my-2 mx-1 border-t border-slate-100" />
              {HIDDEN_NAV.map(renderNavItem)}
            </>
          )}
        </nav>

        {/* Bottom: collapse toggle only */}
        <div className="border-t border-slate-100 p-2">
          <button
            onClick={() => setCollapsed(c => !c)}
            data-testid="sidebar-collapse-btn"
            className={`flex items-center gap-1.5 w-full px-2 py-1 rounded-md text-[10px] text-slate-500 hover:bg-slate-50 transition-colors ${collapsed ? "justify-center" : ""}`}
          >
            {collapsed ? <ChevronRight size={12} /> : <><ChevronLeft size={12} /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Header with Tata Trusts Logo */}
        <header className="bg-[#B71C1C] text-white px-4 flex items-center justify-between shadow-sm" style={{ height: 44, flexShrink: 0 }}>
          <div className="flex items-center gap-3">
            <img 
              src="/tata-trusts-logo.png" 
              alt="Tata Trusts" 
              className="h-4 cursor-pointer hover:opacity-80 transition-opacity"
              style={{ mixBlendMode: 'screen' }}
              onClick={() => navigate("/trustee")}
              data-testid="header-logo-home"
              title="Go to Trustee Overview (home)"
            />
            <div className="w-px h-4 bg-red-400/50" />
            <span className="text-red-100 text-xs">Grant Management Dashboard</span>
            <div className="w-px h-4 bg-red-400/50" />
            <span className="text-white text-xs font-medium">{pageTitle}</span>
          </div>
          <div className="flex items-center gap-3">
            <LastSyncBadge />
            {user && (
              <>
                <div className="w-px h-4 bg-red-400/50" />
                <div
                  className="flex items-center gap-1.5 text-[10px] text-red-100"
                  data-testid="user-pill"
                  title={user.email}
                >
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-[9px]">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </div>
                  <span className="max-w-[140px] truncate">{user.name || user.email}</span>
                </div>
                <button
                  type="button"
                  onClick={logout}
                  data-testid="logout-btn"
                  title="Sign out"
                  className="flex items-center gap-1 text-[10px] text-red-100 hover:text-white hover:bg-white/10 px-1.5 py-0.5 rounded transition-colors"
                >
                  <LogOut size={11} />
                  Sign out
                </button>
              </>
            )}
          </div>
        </header>

        {/* Global Filters — hidden on Grants Overview; simplified on Trustee Overview */}
        {location.pathname !== "/grant-overview" && (
          <GlobalFilters simplified={location.pathname === "/trustee"} />
        )}

        {/* Loading Bar */}
        {globalLoading && (
          <div className="h-0.5 w-full bg-slate-100 overflow-hidden" data-testid="global-loading-bar">
            <div className="h-full bg-[#B71C1C] animate-loading-bar" />
          </div>
        )}

        {/* Page Content */}
        <main className={`flex-1 overflow-y-auto px-4 py-2 transition-opacity duration-150 ${globalLoading ? "opacity-50 pointer-events-none" : "opacity-100"}`}>
          {children}
        </main>
      </div>
    </div>
  );
}
